import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/** Server functions for CTP (Coach Training Program) queries */

const DirectoryCTPsSchema = z.object({
  page: z.number().min(1).optional(),
  limit: z.number().min(1).max(100).optional(),
  search: z.string().optional(),
});

export const getDirectoryCTPs = createServerFn({ method: "GET" })
  .validator(DirectoryCTPsSchema)
  .handler(async ({ data }) => {
    const { getDirectoryCTPs: dbGetDirectoryCTPs } =
      await import("~/lib/server/ctps");
    return dbGetDirectoryCTPs(data);
  });

export const getCtpBySlug = createServerFn({ method: "GET" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }) => {
    const { getCtpBySlug: dbGetCtpBySlug } = await import("~/lib/server/ctps");
    return { ctp: await dbGetCtpBySlug(data.slug) };
  });

export const getCoachesByCtpSlug = createServerFn({ method: "GET" })
  .validator(
    z.object({
      slug: z.string(),
      page: z.number().int().min(1).optional(),
      limit: z.number().int().min(1).max(100).optional(),
    }),
  )
  .handler(async ({ data }) => {
    const { getCoachesByCtpSlug: dbGetCoachesByCtpSlug } =
      await import("~/lib/server/ctps");
    return dbGetCoachesByCtpSlug(data.slug, {
      page: data.page,
      limit: data.limit,
    });
  });

/**
 * Fetches all published CTPs with their accrediting CCO data.
 * Flat list — callers group as needed. Used by the public training-programs index.
 */
export const getAllPublishedCtps = createServerFn({ method: "GET" }).handler(
  async () => {
    const { and, asc, count, eq, isNull } = await import("drizzle-orm");
    const { publicProgramClaim } = await import("~/lib/server/public-claims");
    const { getDBWithInit } = await import("~/lib/db");
    const { ctps, coachesCtps, coaches, profiles, ccos, ctpFrameworks } =
      await import("~/../drizzle/schema");
    const { ContentStatus } = await import("~/lib/schemas/db-enums-cih");
    const { resolveStoredImageUrl } = await import("~/lib/server/s3");
    const { getDiscoveryEligiblePublicCoachFilters } =
      await import("~/lib/server/discovery-coach-filters");

    const db = await getDBWithInit();
    const visibleCoachFilters = await getDiscoveryEligiblePublicCoachFilters();

    const [results, visibleCoachCountRows, frameworkCountRows] =
      await Promise.all([
        db
          .select({
            id: ctps.id,
            name: ctps.name,
            slug: ctps.slug,
            website: ctps.website,
            logoUrl: ctps.logoUrl,
            description: ctps.description,
            deliveryFormat: ctps.deliveryFormat,
            accreditationLevel: ctps.accreditationLevel,
            duration: ctps.duration,
            tuitionRange: ctps.tuitionRange,
            cco: {
              name: ccos.name,
              slug: ccos.slug,
              logoUrl: ccos.logoUrl,
            },
          })
          .from(ctps)
          .innerJoin(ccos, eq(ctps.ccoId, ccos.id))
          .where(
            and(eq(ctps.status, ContentStatus.Published), isNull(ctps.draftOf)),
          )
          .orderBy(asc(ctps.name)),
        db
          .select({
            ctpId: coachesCtps.ctpId,
            total: count(coachesCtps.coachId),
          })
          .from(coachesCtps)
          .innerJoin(
            coaches,
            and(eq(coachesCtps.coachId, coaches.id), publicProgramClaim()),
          )
          .innerJoin(profiles, eq(profiles.coachId, coaches.id))
          .where(and(...visibleCoachFilters))
          .groupBy(coachesCtps.ctpId),
        db
          .select({
            ctpId: ctpFrameworks.ctpId,
            total: count(ctpFrameworks.frameworkId),
          })
          .from(ctpFrameworks)
          .groupBy(ctpFrameworks.ctpId),
      ]);

    const coachCountByCtpId = new Map(
      visibleCoachCountRows.map((row) => [row.ctpId, row.total] as const),
    );
    const frameworkCountByCtpId = new Map(
      frameworkCountRows.map((r) => [r.ctpId, r.total] as const),
    );

    return results
      .map((result) => {
        const resolvedLogoUrl = resolveStoredImageUrl(result.logoUrl);
        const resolvedCcoLogoUrl = resolveStoredImageUrl(result.cco.logoUrl);
        const coaches = coachCountByCtpId.get(result.id) ?? 0;

        return {
          ...result,
          coaches,
          logoUrl: resolvedLogoUrl,
          cco: { ...result.cco, logoUrl: resolvedCcoLogoUrl },
          displayLogoUrl: resolvedLogoUrl ?? resolvedCcoLogoUrl,
          frameworks: frameworkCountByCtpId.get(result.id) ?? 0,
        };
      })
      .sort(
        (left, right) =>
          right.coaches - left.coaches || left.name.localeCompare(right.name),
      );
  },
);
